/**
 * Risk Alerts page — shows all OSINT risk signals across vendors.
 * Allows viewing details, clicking through to source, and resolving alerts.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface RiskAlert {
  RiskAlertId: number;
  VendorName: string;
  VendorId: number;
  SignalType: string;
  Severity: string;
  Summary: string;
  SourceUrl: string | null;
  DetectionDate: string;
  IsResolved: boolean;
}

export function RiskAlerts() {
  const [alerts, setAlerts] = useState<RiskAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'High' | 'Medium' | 'Low'>('all');

  useEffect(() => {
    fetch('/api/vendors/risk-alerts/recent', { credentials: 'include' })
      .then(r => r.json())
      .then(data => setAlerts(Array.isArray(data) ? data : []))
      .catch(() => setAlerts([]))
      .finally(() => setLoading(false));
  }, []);

  const resolve = async (alertId: number) => {
    const res = await fetch(`/api/vendors/risk-alerts/${alertId}/resolve`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notes: 'Reviewed and dismissed' }),
    });
    if (res.ok) {
      setAlerts(prev => prev.filter(a => a.RiskAlertId !== alertId));
    }
  };

  const filtered = filter === 'all' ? alerts : alerts.filter(a => a.Severity === filter);

  if (loading) return <p className="p-6 text-slate-500">Loading risk alerts...</p>;

  return (
    <div className="max-w-5xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-lg font-semibold text-text-primary">Vendor Risk Alerts</h1>
        <div className="flex gap-2">
          {(['all', 'High', 'Medium', 'Low'] as const).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                filter === f ? 'border-baker-accent bg-baker-red/5 text-baker-red' : 'border-border text-slate-600 hover:border-baker-accent/40'
              }`}
            >
              {f === 'all' ? `All (${alerts.length})` : `${f} (${alerts.filter(a => a.Severity === f).length})`}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center">
          <p className="text-emerald-700 font-medium">No unresolved risk alerts.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(alert => (
            <div key={alert.RiskAlertId} className="bg-white rounded-lg border border-border p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${
                      alert.Severity === 'High' ? 'bg-red-100 text-red-700' :
                      alert.Severity === 'Medium' ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {alert.Severity}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700">{alert.SignalType}</span>
                    <span className="text-xs text-slate-400">{new Date(alert.DetectionDate).toLocaleDateString()}</span>
                  </div>
                  <div className="mb-1">
                    <Link to={`/vendors/${alert.VendorId}`} className="text-sm font-semibold text-baker-red no-underline hover:underline">
                      {alert.VendorName}
                    </Link>
                  </div>
                  <p className="text-sm text-slate-600 whitespace-pre-wrap">{alert.Summary}</p>
                  {alert.SourceUrl && (
                    <a href={alert.SourceUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-baker-red no-underline hover:underline mt-1 inline-block">
                      View source ↗
                    </a>
                  )}
                </div>
                <button
                  onClick={() => resolve(alert.RiskAlertId)}
                  className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium text-slate-600 hover:bg-page hover:border-baker-accent/40 transition-all whitespace-nowrap"
                >
                  Dismiss
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
